import React from 'react';

import Button from '../../library/button/button';
import Image from '../../library/image/image';
import Pagination from '../../library/pagination/pagination';
import Select from '../../library/select/select';
import Typography from '../../library/typography/typography';
import { EditRegularIcon, SlidersRegularIcon } from '../../shared/icons/icons';

import { useModal } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { formatToTitleCase } from '../../shared/utils/format/format';
import { localStorage } from '../../shared/utils/storage/storage';

import { SHOP_TIRES_FLOW_MODAL } from '../../shared/constants/modals';
import { TREADWELL_VEHICLE_PREFERENCES } from '../../shared/constants/storage-keys';

import './results-bar.scss';

interface ResultsBarProps {
  currentPage: number;
  currentSort: string;
  filterCount: number;
  formattedTireSize: string;
  hideSize: boolean;
  isTreadwellFlow: boolean;
  numberOfPages: number;
  onPageChange: (pageNumber: number) => void;
  onSelectSort: (data) => void;
  onToggleMobileFilter: () => void;
  recommendationAvailable: boolean;
  resultsText: string;
  showFilter: boolean;
  sorts: string[];
  totalNumberOfResults: string | number;
  wheelSize: string;
}

function ResultsBar(props: ResultsBarProps) {
  const {
    currentSort,
    filterCount,
    formattedTireSize,
    hideSize,
    onPageChange,
    onSelectSort,
    onToggleMobileFilter,
    currentPage,
    isTreadwellFlow = false,
    numberOfPages,
    resultsText,
    recommendationAvailable = false,
    showFilter = false,
    sorts,
    totalNumberOfResults,
    wheelSize
  } = props;

  const { openModal } = useModal();

  let city, state, isCustomDriverTypeShowingChecked, selectedDriverTypeChecked;

  if (isTreadwellFlow && localStorage(TREADWELL_VEHICLE_PREFERENCES)) {
    const { preferences } = localStorage(TREADWELL_VEHICLE_PREFERENCES);

    const {
      isCustomDriverTypeShowing,
      selectedDriverType,
      selectedCustomDrivingPrioritiesType,
      zipCodeWeatherData
    } = preferences;

    isCustomDriverTypeShowingChecked = isCustomDriverTypeShowing;
    ({ city, state } = zipCodeWeatherData);
    city = city.toLowerCase();

    if (isCustomDriverTypeShowingChecked) {
      selectedDriverTypeChecked = formatToTitleCase(
        selectedCustomDrivingPrioritiesType
      );
    } else {
      selectedDriverTypeChecked = selectedDriverType.name;
    }
  }

  let sortOptions;
  if (sorts && typeof sorts === 'object' && Object.keys(sorts).length > 0) {
    sortOptions = Object.keys(sorts).map(sort => sorts[sort]);
  }

  return (
    <div
      className="results-bar"
      styleName={`container ${
        numberOfPages === 1 ? 'container--single-page' : ''
      }`}
    >
      {showFilter && (
        <Button
          className="results-bar__filter-button"
          isSecondary
          onClick={onToggleMobileFilter}
          styleName="filter-button"
        >
          <SlidersRegularIcon
            className="results-bar__filter-icon"
            styleName="filter-button-icon"
          />
          filters
          {!isNaN(filterCount) && filterCount > 0 && (
            <span styleName="filter-button-count">({filterCount})</span>
          )}
        </Button>
      )}

      <div className="results-bar__count-line" styleName="count-line">
        {!hideSize && !window.isPreRendering && (
          <>
            {formattedTireSize && (
              <span styleName="tire-size">
                <span>Shop {formattedTireSize} Tires</span>
                <span styleName="spacer"> | </span>
              </span>
            )}

            {wheelSize && (
              <span styleName="wheel-size">
                <span>Shop {wheelSize} Wheels</span>
                <span styleName="spacer"> | </span>
              </span>
            )}
          </>
        )}

        {!window.isPreRendering &&
          (isTreadwellFlow && recommendationAvailable ? (
            <div styleName="treadwell-results-message">
              <Image
                alt="Treadwell icon"
                height="52"
                src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell-icon-only-trademark.svg"
                styleName="icon"
              />
              <Typography tag="span">
                Here are the&nbsp;
                <strong>top {totalNumberOfResults} matches </strong>
                for&nbsp;
                {isCustomDriverTypeShowingChecked ? (
                  <span styleName="custom-driver-type">
                    {selectedDriverTypeChecked}
                  </span>
                ) : (
                  selectedDriverTypeChecked
                )}
                &nbsp;in
                <span styleName="store-location">&nbsp;{city}</span>
                ,&nbsp;{state}!&nbsp;
                <Button
                  onClick={() => {
                    trackAction('treadwell_plp_change_priorities_click');
                    openModal(SHOP_TIRES_FLOW_MODAL, {
                      hideZipBackButton: true,
                      shouldStartAtZip: true
                    });
                  }}
                  styleName="edit-button"
                >
                  <EditRegularIcon />
                  edit
                </Button>
              </Typography>
            </div>
          ) : (
            <span styleName="number-of-results">
              <strong>{totalNumberOfResults || 0}</strong> {resultsText || ''}
            </span>
          ))}
      </div>

      {process.env.__FF_LOAD_MORE__ !== 'true' &&
        numberOfPages &&
        numberOfPages > 0 && (
          <Pagination
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            numberOfPagesToShow={3}
            onPageChange={onPageChange}
            styleName="pagination"
          />
        )}

      {currentSort && sorts?.[currentSort] && (
        <div className="results-bar__select-wrapper" styleName="select-wrapper">
          <Select
            label="Sort"
            onChange={data => onSelectSort(data)}
            options={sortOptions}
            value={sorts[currentSort]}
          />
        </div>
      )}
    </div>
  );
}

export default ResultsBar;
